import { useId, useState, type FormEvent, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import {
  INVITABLE_ROLES,
  PAYABLE_STAGES,
  inviteStaffSchema,
  type InvitableRole,
  type PayableStage,
} from '@tailoring-crm/shared';
import type { PlanRefusal } from '@tailoring-crm/shared';
import { isApiRequestError } from '../../lib/api/errors.js';
import { planRefusalFrom } from '../../lib/billing/plan-refusal.js';
import { useInviteStaff } from '../../lib/staff/use-staff.js';
import { UpgradePrompt } from '../billing/upgrade-prompt.js';
import {
  cn,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  useToast,
} from '@tailoring-crm/ui';

/**
 * Inviting someone to the shop.
 *
 * Three decisions and no more: their number, what they are called, and whether they run the
 * shop with you or stitch for it. Specialisations are offered here because an owner adding a
 * finisher knows it at that moment and will not come back to set it later — but they are
 * optional, and nothing in the product blocks on them.
 *
 * The 409 is not a generic failure and is not shown as one: "this number is already on your
 * staff list" and "this number belonged to someone who left" are different situations with
 * different next actions, and the second one has to say "re-activate them" or an owner will
 * make a second, empty record for a person whose history is sitting right there.
 */

interface InviteStaffDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function InviteStaffDialog({ open, onOpenChange }: InviteStaffDialogProps): ReactElement {
  const { t } = useTranslation();
  const { toast } = useToast();
  const formId = useId();
  const invite = useInviteStaff();

  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState<InvitableRole>('tailor');
  const [specialisations, setSpecialisations] = useState<PayableStage[]>([]);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string[]>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [refusal, setRefusal] = useState<PlanRefusal | null>(null);

  const reset = (): void => {
    setEmail('');
    setPhone('');
    setName('');
    setRole('tailor');
    setSpecialisations([]);
    setFieldErrors({});
    setFormError(null);
    setRefusal(null);
  };

  const close = (next: boolean): void => {
    if (!next) reset();
    onOpenChange(next);
  };

  const toggleStage = (stage: PayableStage): void => {
    setSpecialisations((previous) =>
      previous.includes(stage) ? previous.filter((one) => one !== stage) : [...previous, stage],
    );
  };

  async function onSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setFormError(null);
    setRefusal(null);

    // The SHARED schema, so the browser rejects exactly what the endpoint would — including
    // the mobile-number normalisation that turns `+91 98765 43210` into one canonical number.
    const parsed = inviteStaffSchema.safeParse({
      email,
      // Optional: an empty box means "no number on file", not an invalid one.
      ...(phone.trim() === '' ? {} : { phone }),
      name,
      role,
      specialisations,
    });
    if (!parsed.success) {
      const errors: Record<string, string[]> = {};
      for (const issue of parsed.error.issues) {
        const key = issue.path[0] === undefined ? '_' : String(issue.path[0]);
        (errors[key] ??= []).push(issue.message);
      }
      setFieldErrors(errors);
      return;
    }
    setFieldErrors({});

    try {
      const created = await invite.mutateAsync(parsed.data);
      toast({ title: t('staff.inviteSent', { name: created.name }) });
      close(false);
    } catch (error) {
      /**
       * The seat limit, rendered where they were stopped rather than as a generic failure.
       * Somebody who has just typed a karigar's number and been refused is at the exact
       * moment they are most willing to pay — and the only moment they have the context to
       * decide whether it is worth it. Sending them to a billing screen to work out what
       * went wrong loses both.
       */
      const limit = planRefusalFrom(error);
      if (limit) {
        setRefusal(limit);
        return;
      }
      // The server's own words: "already on your staff list" and "re-activate them instead" are
      // different situations with different next actions, and only the server knows which.
      if (isApiRequestError(error) && error.status === 409) {
        setFormError(error.message);
        return;
      }
      setFormError(t('error.unexpected'));
    }
  }

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('staff.inviteTitle')}</DialogTitle>
          <DialogDescription>{t('staff.inviteDescription')}</DialogDescription>
        </DialogHeader>

        <form
          noValidate
          id={formId}
          className="flex flex-col gap-4"
          onSubmit={(event) => void onSubmit(event)}
        >
          <div className="flex flex-col gap-1.5">
            <Label htmlFor={`${formId}-email`}>{t('staff.field.email')}</Label>
            <Input
              id={`${formId}-email`}
              // This is the field that identifies them, and the one the join code is sent to.
              type="email"
              inputMode="email"
              autoComplete="off"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              aria-invalid={fieldErrors.email ? true : undefined}
              autoFocus
            />
            {fieldErrors.email ? (
              <p className="text-sm text-destructive">{fieldErrors.email[0]}</p>
            ) : (
              <p className="text-sm text-muted-foreground">{t('staff.emailHint')}</p>
            )}
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor={`${formId}-phone`}>{t('staff.field.phone')}</Label>
            <Input
              id={`${formId}-phone`}
              type="tel"
              inputMode="numeric"
              autoComplete="off"
              value={phone}
              onChange={(event) => setPhone(event.target.value)}
              aria-invalid={fieldErrors.phone ? true : undefined}
            />
            {fieldErrors.phone ? (
              <p className="text-sm text-destructive">{fieldErrors.phone[0]}</p>
            ) : (
              <p className="text-sm text-muted-foreground">{t('staff.phoneHint')}</p>
            )}
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor={`${formId}-name`}>{t('staff.field.name')}</Label>
            <Input
              id={`${formId}-name`}
              value={name}
              onChange={(event) => setName(event.target.value)}
              aria-invalid={fieldErrors.name ? true : undefined}
            />
            {fieldErrors.name ? (
              <p className="text-sm text-destructive">{fieldErrors.name[0]}</p>
            ) : null}
          </div>

          <fieldset className="flex flex-col gap-2">
            <legend className="text-sm font-medium">{t('staff.field.role')}</legend>
            <div className="flex gap-2">
              {INVITABLE_ROLES.map((one) => (
                <button
                  key={one}
                  type="button"
                  aria-pressed={role === one}
                  onClick={() => setRole(one)}
                  className={cn(
                    'flex-1 rounded-md border px-3 py-2 text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                    role === one
                      ? 'border-transparent bg-primary text-primary-foreground'
                      : 'border-border hover:bg-accent',
                  )}
                >
                  {t(`role.${one}`)}
                </button>
              ))}
            </div>
            <p className="text-sm text-muted-foreground">
              {role === 'tailor' ? t('staff.roleHint.tailor') : t('staff.roleHint.manager')}
            </p>
          </fieldset>

          <fieldset className="flex flex-col gap-2">
            <legend className="text-sm font-medium">{t('staff.field.specialisations')}</legend>
            <div className="flex flex-wrap gap-2">
              {PAYABLE_STAGES.map((stage) => {
                const active = specialisations.includes(stage);
                return (
                  <button
                    key={stage}
                    type="button"
                    aria-pressed={active}
                    onClick={() => toggleStage(stage)}
                    className={cn(
                      'rounded-full border px-3 py-1.5 text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                      active
                        ? 'border-transparent bg-primary text-primary-foreground'
                        : 'border-border hover:bg-accent',
                    )}
                  >
                    {t(`stage.${stage}`)}
                  </button>
                );
              })}
            </div>
            <p className="text-sm text-muted-foreground">{t('staff.specialisationsHint')}</p>
          </fieldset>

          {refusal ? <UpgradePrompt refusal={refusal} compact /> : null}

          {formError ? (
            <p role="alert" className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
              {formError}
            </p>
          ) : null}
        </form>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => close(false)}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" form={formId} disabled={invite.isPending}>
            {invite.isPending ? t('common.saving') : t('staff.sendInvite')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
